import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { FilterClause, FilterOp, WhereGroup } from '../api/client';
import { ConditionBuilder, countConditions } from './ConditionBuilder';
import { OP_LABELS, opsForType, type FieldMeta, type ResourceMeta } from '../meta/types';
import { Button } from '../design/primitives/Button';
import { Chip } from '../design/primitives/Misc';
import { Popover } from '../design/primitives/Overlay';
import { Checkbox, Field, Input, Select } from '../design/primitives/Input';
import { FieldEditor } from '../fields/registry';
import { Icon } from '../design/icons';
import { formatDate, formatMoney, formatMinutes } from '../../lib/format';
import { useRelationLabel, useRelationSearch } from '../fields/RelationPicker';

// Filters bound to the query DSL. Each active filter is one FilterClause shown as a chip; clicking
// it (or a field's filter button) opens the FilterPopover (condition + typed value editor).

function RelValue({ field, id }: { field: FieldMeta; id: string }) {
  const rel = useRelationLabel(field.relation, id);
  return <>{rel.data ?? (rel.isLoading ? '…' : `Unknown ${field.label.toLowerCase()}`)}</>;
}

function ValueLabel({ field, clause }: { field: FieldMeta; clause: FilterClause }) {
  if (clause.op === 'is_null') return <>{clause.value ? 'empty' : 'not empty'}</>;
  const v = clause.value;
  const one = (x: unknown, i: number) => {
    if (x == null) return '—';
    if (field.type === 'relation') return <RelValue key={i} field={field} id={String(x)} />;
    if (field.type === 'enum') return field.enum?.find((o) => o.value === String(x))?.label ?? String(x);
    if (field.type === 'money') return formatMoney(x as string, 'USD').replace(/^\$/, '');
    if (field.type === 'date' || field.type === 'datetime') return formatDate(String(x));
    if (field.type === 'duration') return formatMinutes(x as number);
    if (field.type === 'boolean') return x === true || x === 'true' ? 'yes' : 'no';
    return String(x);
  };
  if (Array.isArray(v)) {
    if (clause.op === 'between') return <>{one(v[0], 0)} – {one(v[1], 1)}</>;
    return <>{v.map((x, i) => <span key={i}>{i > 0 && ', '}{one(x, i)}</span>)}</>;
  }
  return <>{one(v, 0)}</>;
}

/** Field types that make a good one-click filter button (a short list of values to pick from). */
const QUICK_TYPES = new Set<FieldMeta['type']>(['enum', 'relation', 'boolean']);
const MAX_QUICK = 5;

/** The fields offered as one-click filter buttons: the screen's choice, else the list's first pickable columns. */
export function quickFilterFields(meta: ResourceMeta, locked: string[] = [], quick?: string[]): FieldMeta[] {
  const usable = (f: FieldMeta | undefined): f is FieldMeta => Boolean(f && f.filterable && !locked.includes(f.name));
  if (quick) return quick.map((n) => meta.fields.find((f) => f.name === n)).filter(usable);
  return meta.fields.filter((f) => usable(f) && f.inList !== false && QUICK_TYPES.has(f.type) && f.name !== 'tenant_id').slice(0, MAX_QUICK);
}

/** The condition a new filter on this field starts with: several values for an enum, one record for a relation. */
function defaultOp(field: FieldMeta): FilterOp {
  const ops = field.ops ?? opsForType(field.type);
  if (field.type === 'enum' && ops.includes('in')) return 'in';
  return ops[0]!;
}

/**
 * Filters bound to the query DSL. The list's main fields are one-click buttons ("Status ▾",
 * "Priority ▾" …) that open the value picker straight away; an active filter shows its value on the
 * button. Every other filterable field is one search away under "More filters".
 */
export function FilterChips({ meta, filters, onChange, locked = [], quick, where, onWhereChange }: { meta: ResourceMeta; filters: FilterClause[]; onChange: (next: FilterClause[]) => void; locked?: string[]; quick?: string[]; where?: WhereGroup | null; onWhereChange?: (w: WhereGroup | null) => void }) {
  const anchors = useRef(new Map<string, HTMLElement | null>());
  const moreRef = useRef<HTMLButtonElement>(null);
  // What the popover edits: an existing clause (by index) or a new one on a field, anchored to its button.
  const [open, setOpen] = useState<{ field: FieldMeta; index: number | null; anchorKey: string } | null>(null);
  const [picking, setPicking] = useState(false);
  const [building, setBuilding] = useState(false);
  const nConditions = countConditions(where);
  const quickFields = useMemo(() => quickFilterFields(meta, locked, quick), [meta, locked, quick]);
  const quickNames = new Set(quickFields.map((f) => f.name));
  // Fields a person would filter by: shown somewhere (list or detail), never bare ids.
  const filterable = meta.fields.filter((f) => f.filterable && !locked.includes(f.name) && f.name !== 'tenant_id' && f.name !== meta.idField && (f.inList !== false || f.inDetail !== false));
  const setAnchor = (key: string) => (el: HTMLElement | null) => {
    anchors.current.set(key, el);
  };
  const indexOf = (name: string) => filters.findIndex((c) => c.field === name);

  const chipFor = (f: FieldMeta, i: number, key: string) => {
    const c = filters[i]!;
    return (
      <span key={key} ref={setAnchor(key)}>
        <Chip active onClick={() => setOpen({ field: f, index: i, anchorKey: key })} onRemove={() => onChange(filters.filter((_, j) => j !== i))}>
          <span className="hf-muted" style={{ color: 'inherit', opacity: 0.7 }}>
            {f.label}
          </span>{' '}
          {c.op === 'in' || c.op === 'eq' ? '' : `${OP_LABELS[c.op]} `}
          <ValueLabel field={f} clause={c} />
        </Chip>
      </span>
    );
  };

  return (
    <div className="hf-chiprow hf-filterbar" role="group" aria-label="Filters">
      {quickFields.map((f) => {
        const i = indexOf(f.name);
        const key = `quick:${f.name}`;
        if (i >= 0) return chipFor(f, i, key);
        return (
          <span key={key} ref={setAnchor(key)}>
            <Button size="sm" variant="ghost" className="hf-filter-btn" aria-haspopup="dialog" onClick={() => setOpen({ field: f, index: null, anchorKey: key })}>
              {f.label}
              <Icon name="chevron-down" size={12} />
            </Button>
          </span>
        );
      })}
      {filters.map((c, i) => {
        const f = meta.fields.find((x) => x.name === c.field);
        if (!f || locked.includes(c.field) || (quickNames.has(c.field) && indexOf(c.field) === i)) return null;
        return chipFor(f, i, `chip:${i}`);
      })}
      <Button ref={moreRef} size="sm" variant="ghost" icon="filter" aria-haspopup="dialog" aria-expanded={picking} onClick={() => setPicking((v) => !v)}>
        {quickFields.length ? 'More filters' : 'Filter'}
      </Button>
      {onWhereChange &&
        (nConditions > 0 ? (
          <Chip active icon="sliders-horizontal" onClick={() => setBuilding(true)} onRemove={() => onWhereChange(null)}>
            {nConditions} condition{nConditions === 1 ? '' : 's'} ({'any' in where! ? 'any' : 'all'})
          </Chip>
        ) : (
          <Button size="sm" variant="ghost" icon="sliders-horizontal" aria-haspopup="dialog" onClick={() => setBuilding(true)}>
            Conditions
          </Button>
        ))}
      {building && onWhereChange && (
        <ConditionBuilder
          meta={meta}
          where={where ?? null}
          open
          onClose={() => setBuilding(false)}
          onApply={(w) => {
            onWhereChange(w);
            setBuilding(false);
          }}
        />
      )}
      {picking && (
        <FieldPicker
          anchor={moreRef}
          fields={filterable}
          onPick={(f) => {
            setPicking(false);
            setOpen({ field: f, index: null, anchorKey: 'more' });
          }}
          onClose={() => setPicking(false)}
        />
      )}
      {open && QUICK_TYPES.has(open.field.type) && (open.index == null || ['eq', 'in'].includes(filters[open.index]!.op)) && (
        <ValueChecklist
          anchor={open.anchorKey === 'more' ? moreRef : anchors.current.get(open.anchorKey) ?? moreRef.current}
          field={open.field}
          clause={open.index != null ? filters[open.index] : undefined}
          onChange={(next) => {
            const rest = filters.filter((_, j) => j !== open.index);
            if (!next) {
              onChange(rest);
              setOpen(null);
              return;
            }
            // Keep the chip where it was; a new one goes last and is edited in place from now on.
            if (open.index != null) onChange(filters.map((c, j) => (j === open.index ? next : c)));
            else {
              onChange([...filters, next]);
              setOpen({ ...open, index: filters.length });
            }
          }}
          onClose={() => setOpen(null)}
        />
      )}
      {open && !(QUICK_TYPES.has(open.field.type) && (open.index == null || ['eq', 'in'].includes(filters[open.index]!.op))) && (
        <FilterPopover
          anchor={open.anchorKey === 'more' ? moreRef : anchors.current.get(open.anchorKey) ?? moreRef.current}
          field={open.field}
          clause={open.index != null ? filters[open.index] : undefined}
          onApply={(next) => {
            onChange(open.index != null ? filters.map((c, j) => (j === open.index ? next : c)) : [...filters, next]);
            setOpen(null);
          }}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  );
}

interface Choice {
  value: string;
  label: string;
}

/**
 * The value picker of a pickable field (Status, Priority, Queue, Assignee …): a searchable checklist.
 * Every tick applies at once — one value is `is` (which a connected system can usually filter on
 * itself), several are `is any of` — and unticking the last one removes the filter.
 */
function ValueChecklist({ anchor, field, clause, onChange, onClose }: { anchor: RefObject<HTMLElement | null> | HTMLElement | null; field: FieldMeta; clause?: FilterClause; onChange: (c: FilterClause | null) => void; onClose: () => void }) {
  const initial = clause ? (Array.isArray(clause.value) ? clause.value.map(String) : [String(clause.value)]) : [];
  const [picked, setPicked] = useState<string[]>(initial);
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(q.trim()), 200);
    return () => window.clearTimeout(t);
  }, [q]);
  const search = useRelationSearch(field.relation ?? { resource: '', displayField: 'name' });
  const remote = useQuery({
    queryKey: ['filter-values', field.relation?.resource, field.relation?.valueField ?? 'id', JSON.stringify(field.relation?.filter ?? {}), debounced],
    enabled: field.type === 'relation' && Boolean(field.relation),
    staleTime: 60_000,
    queryFn: () => search(debounced),
  });
  const [seen, setSeen] = useState<Record<string, string>>({});
  const choices: Choice[] = useMemo(() => {
    if (field.type === 'boolean') return [{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }];
    if (field.type === 'enum') {
      const all = (field.enum ?? []).map((o) => ({ value: o.value, label: o.label }));
      const needle = q.trim().toLowerCase();
      return needle ? all.filter((o) => o.label.toLowerCase().includes(needle)) : all;
    }
    return (remote.data ?? []).map((o) => ({ value: o.value, label: o.label }));
  }, [field, q, remote.data]);
  useEffect(() => {
    if (choices.length) setSeen((s) => ({ ...s, ...Object.fromEntries(choices.map((c) => [c.value, c.label])) }));
  }, [choices]);

  const apply = (next: string[]) => {
    setPicked(next);
    if (next.length === 0) return onChange(null);
    const cast = (v: string): unknown => (field.type === 'boolean' ? v === 'true' : v);
    onChange(next.length === 1 ? { field: field.name, op: 'eq', value: cast(next[0]!) } : { field: field.name, op: 'in', value: next.map(cast) });
  };
  const toggle = (v: string) => apply(picked.includes(v) ? picked.filter((x) => x !== v) : [...picked, v]);
  // Ticked values stay listed (and first) even when the search no longer matches them.
  const shown: Choice[] = [...picked.filter((v) => !choices.some((c) => c.value === v)).map((v) => ({ value: v, label: seen[v] ?? v })), ...choices];
  const searchable = field.type === 'relation' || (field.enum?.length ?? 0) > 8;

  return (
    <Popover open onClose={onClose} anchor={anchor} pad trapFocus aria-label={`Filter ${field.label}`}>
      <div className="hf-stack" style={{ minWidth: 240 }}>
        <div className="hf-between">
          <span className="hf-caps">{field.label}</span>
          {picked.length > 0 && (
            <Button size="sm" variant="ghost" onClick={() => apply([])}>
              Clear
            </Button>
          )}
        </div>
        {searchable && <Input size="sm" autoFocus placeholder={`Find ${field.label.toLowerCase()}…`} aria-label={`Find ${field.label.toLowerCase()}`} value={q} onChange={(e) => setQ(e.target.value)} />}
        <div className="hf-field-picker" role="group" aria-label={`${field.label} values`} aria-busy={remote.isFetching || undefined}>
          {remote.isLoading && <div className="hf-muted hf-row" style={{ padding: '6px 8px', gap: 6 }}><span className="hf-spinner" aria-hidden /> Loading…</div>}
          {!remote.isLoading && shown.length === 0 && <div className="hf-muted" style={{ padding: '6px 8px' }}>{q ? `Nothing matches “${q}”.` : 'No values.'}</div>}
          {shown.map((c) => (
            <div key={c.value} className="hf-field-option">
              <Checkbox checked={picked.includes(c.value)} onChange={() => toggle(c.value)} label={c.label} />
            </div>
          ))}
        </div>
        <div className="hf-form-actions" style={{ paddingTop: 4 }}>
          <Button size="sm" variant="primary" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </Popover>
  );
}

/** "More filters": every filterable field, narrowed by typing — not a menu as long as the schema. */
function FieldPicker({ anchor, fields, onPick, onClose }: { anchor: RefObject<HTMLElement | null>; fields: FieldMeta[]; onPick: (f: FieldMeta) => void; onClose: () => void }) {
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const needle = q.trim().toLowerCase();
  const shown = needle ? fields.filter((f) => f.label.toLowerCase().includes(needle) || f.name.includes(needle)) : fields;
  useEffect(() => setActive(0), [needle]);
  return (
    <Popover open onClose={onClose} anchor={anchor} pad trapFocus aria-label="Filter by field">
      <div className="hf-stack" style={{ minWidth: 240 }}>
        <Input
          size="sm"
          autoFocus
          placeholder="Filter by…"
          aria-label="Find a field"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setActive((a) => Math.min(a + 1, shown.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActive((a) => Math.max(a - 1, 0));
            } else if (e.key === 'Enter' && shown[active]) {
              e.preventDefault();
              onPick(shown[active]!);
            }
          }}
        />
        <div className="hf-field-picker" role="listbox" aria-label="Fields">
          {shown.length === 0 && <div className="hf-muted" style={{ padding: '6px 8px' }}>No field matches “{q}”.</div>}
          {shown.map((f, i) => (
            <button key={f.name} type="button" role="option" aria-selected={i === active} className={['hf-field-option', i === active && 'active'].filter(Boolean).join(' ')} onMouseEnter={() => setActive(i)} onClick={() => onPick(f)}>
              {f.label}
            </button>
          ))}
        </div>
      </div>
    </Popover>
  );
}

export function FilterPopover({ anchor, field, clause, onApply, onClose }: { anchor: RefObject<HTMLElement | null> | HTMLElement | null; field: FieldMeta; clause?: FilterClause; onApply: (c: FilterClause) => void; onClose: () => void }) {
  const ops = useMemo(() => field.ops ?? opsForType(field.type), [field]);
  const [op, setOp] = useState<FilterOp>(clause?.op ?? defaultOp(field));
  const [value, setValue] = useState<unknown>(clause?.value ?? (op === 'is_null' ? true : null));
  useEffect(() => {
    if (op === 'is_null' && typeof value !== 'boolean') setValue(true);
    if (op === 'between' && !Array.isArray(value)) setValue([null, null]);
    if ((op === 'in' || op === 'nin') && !Array.isArray(value)) setValue([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [op]);

  const valid = op === 'is_null' ? true : op === 'between' ? Array.isArray(value) && value[0] != null && value[1] != null : op === 'in' || op === 'nin' ? Array.isArray(value) && value.length > 0 : value != null && value !== '';

  const editorFor = (v: unknown, set: (x: unknown) => void, label: string) => (
    <FieldEditor field={{ ...field, widget: undefined, required: false, nullable: true }} value={v} onChange={set} size="sm" autoFocus aria-label={label} onCommit={() => valid && onApply({ field: field.name, op, value })} onCancel={onClose} />
  );

  return (
    <Popover open onClose={onClose} anchor={anchor} pad trapFocus aria-label={`Filter ${field.label}`}>
      <form
        className="hf-stack"
        style={{ minWidth: 260 }}
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) onApply({ field: field.name, op, value });
        }}
      >
        <div className="hf-caps">{field.label}</div>
        <Field label="Condition">
          <Select size="sm" value={op} onChange={(e) => setOp(e.target.value as FilterOp)} options={ops.map((o) => ({ value: o, label: OP_LABELS[o] }))} />
        </Field>
        {op === 'is_null' ? (
          <Field label="Value">
            <Select size="sm" value={value ? 'true' : 'false'} onChange={(e) => setValue(e.target.value === 'true')} options={[{ value: 'true', label: 'is empty' }, { value: 'false', label: 'is not empty' }]} />
          </Field>
        ) : op === 'between' ? (
          <div className="hf-row">
            <Field label="From">{editorFor((value as unknown[])?.[0], (x) => setValue([x, (value as unknown[])?.[1] ?? null]), 'From')}</Field>
            <Field label="To">{editorFor((value as unknown[])?.[1], (x) => setValue([(value as unknown[])?.[0] ?? null, x]), 'To')}</Field>
          </div>
        ) : op === 'in' || op === 'nin' ? (
          <Field label="Values">
            {field.type === 'enum' ? (
              <div className="hf-chiprow">
                {(field.enum ?? []).map((o) => {
                  const arr = (value as string[]) ?? [];
                  const on = arr.includes(o.value);
                  return (
                    <Chip key={o.value} active={on} onClick={() => setValue(on ? arr.filter((x) => x !== o.value) : [...arr, o.value])}>
                      {o.label}
                    </Chip>
                  );
                })}
              </div>
            ) : (
              <Input size="sm" autoFocus placeholder="Comma-separated" value={((value as string[]) ?? []).join(',')} onChange={(e) => setValue(e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} />
            )}
          </Field>
        ) : (
          <Field label="Value">{editorFor(value, setValue, 'Value')}</Field>
        )}
        <div className="hf-form-actions" style={{ paddingTop: 8 }}>
          <Button size="sm" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" type="submit" disabled={!valid}>
            Apply
          </Button>
        </div>
      </form>
    </Popover>
  );
}

/** Quick chips for an enum field: "All | value | value…" with optional counts from loaded rows. */
export function EnumChips({ field, value, onChange, counts, label }: { field: FieldMeta; value: string | null; onChange: (v: string | null) => void; counts?: Record<string, number>; label?: string }) {
  return (
    <div className="hf-chiprow" role="tablist" aria-label={label ?? `Filter by ${field.label.toLowerCase()}`}>
      <Chip role="tab" aria-selected={value == null} active={value == null} onClick={() => onChange(null)}>
        All
      </Chip>
      {(field.enum ?? []).map((o) => (
        <Chip key={o.value} role="tab" aria-selected={value === o.value} active={value === o.value} onClick={() => onChange(o.value)} count={counts?.[o.value] || undefined}>
          {o.label}
        </Chip>
      ))}
    </div>
  );
}

export function SearchBox({ value, onChange, placeholder, inputRef, 'aria-label': ariaLabel }: { value: string; onChange: (v: string) => void; placeholder?: string; inputRef?: RefObject<HTMLInputElement | null>; 'aria-label'?: string }) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  useEffect(() => {
    if (text === value) return;
    const t = window.setTimeout(() => onChange(text), 220);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);
  return (
    <div className="hf-search">
      <Icon name="search" size={14} className="glyph" />
      <input ref={inputRef} className="hf-input" value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder ?? 'Search…'} aria-label={ariaLabel ?? 'Search'} onKeyDown={(e) => e.key === 'Escape' && setText('')} />
      <kbd className="hf-kbd" aria-hidden>
        /
      </kbd>
    </div>
  );
}
